/**
 * 前端通用入参校验。
 *
 * 背景：后端大量字段只做了「非空 / 上限」校验，且部分字段的 proto 长度上限
 * 大于数据库列宽（例如地址标签 @vd<=128 但列宽 32），超长或含 HTML 标签的入参
 * 会直接触发数据库错误，前端只看到「服务器内部错误」。这里在前端提前拦截，
 * 长度上限统一按数据库列宽收敛，见 InputLimit。
 *
 * 两道防线：
 * 1. 输入框的 maxLength 按 InputLimit 限制，用户根本输不超，不产生报错；
 * 2. getTextRules 的长度校验作为兜底，覆盖 Select allowCreate 等非 Input 控件
 *    与程序赋值的场景（这些地方 maxLength 管不到）。
 *
 * 关于中文与英文的长度口径，三层计量单位完全不同，必须只对齐数据库：
 * - 前端 Input maxLength / JS str.length：UTF-16 码元（汉字 1、英文 1、emoji 2）；
 * - 后端 proto @vd 的 len($)：UTF-8 字节（实测 1024 个英文=200 成功，400 个汉字=400 参数错误）；
 * - MySQL varchar(n)：字符数（utf8mb4 下汉字、英文、emoji 均计 1）。
 * 数据库是落库硬约束，所以 InputLimit 全部取列宽（字符数），不参考 @vd 的数字；
 * 现有字段均满足「列宽 × 3 ≤ @vd 字节上限」，中文不会先被 @vd 拦掉。
 */
export const InputLimit = {
  /** 供应商简称 */
  shortName: 6,
  /** 部门 / 角色 / 联系人 / 收货人等姓名类字段 */
  name: 16,
  /** 地址标签 / 物料名称 / 品牌 / 分类名称 */
  label: 32,
  /** 供应商 / 客户名称、组件编码（request_item.component） */
  title: 64,
  /** 邮箱（account.user.email），同时也是登录账号框的长度上限 */
  email: 64,
  /** 密码（account.user.password 列宽 60） */
  password: 60,
  /** 地址、支付期限等描述类字段 */
  detail: 128,
  /** 备注、角色描述等多行文本 */
  remark: 255,
  /** 规格型号等长文本 */
  text: 1024
};

/** 按字符（码点）计数，与 MySQL varchar(n) 的口径一致；JS 的 length 会把 emoji 算 2 个 */
export function charLength(text) {
  return Array.from(text || '').length;
}

/** 按 UTF-8 字节计数，与后端 proto `@vd len($)` 的口径一致 */
export function byteLength(text) {
  if (!text) {
    return 0;
  }
  // TextEncoder 在所有目标浏览器都可用，避免手动算字符宽度
  return new TextEncoder().encode(text).length;
}

/**
 * 按 UTF-8 字节硬截断，用于后端 `@vd len($)` 按字节计数的字段。
 * maxLength 只能按字符数拦（64 个汉字 = 192 字节），这种字段需要它来兜住字节上限，
 * 配合 Form.Item 的 normalize 使用，表现与 maxLength 一致：输到上限就停。
 */
export function truncateByBytes(text, maxBytes) {
  const value = text || '';
  if (byteLength(value) <= maxBytes) {
    return value;
  }
  let result = '';
  let used = 0;
  // 按码点遍历，避免把 emoji 之类的代理对截成半个字符
  for (const char of Array.from(value)) {
    const size = byteLength(char);
    if (used + size > maxBytes) {
      break;
    }
    result += char;
    used += size;
  }
  return result;
}

/**
 * 生成 Form.Item 的 normalize：按 UTF-8 字节硬截断。
 * 后端不少检索字段按 64 字节限制（22 个汉字就 400），maxLength 只能拦字符数，
 * 这类字段必须配它才能真拦住。
 */
export function limitBytes(maxBytes) {
  return value => truncateByBytes(String(value ?? ''), maxBytes);
}

/** 控制字符（含 \u0000、\u001b 等，前端输入框换行外的不可见字符一律视为非法） */
const ControlCharReg = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/;
/**
 * 常见 HTML 标签名：只拦这些真实标签，避免把 `<M10>`、`<Ra1.6>` 这类工程规格误判为标签。
 * 标签后必须是词边界，所以 `<math>` 能拦、`<M10>` 不会命中（m 后面是数字，凑不成 math）。
 */
const HtmlTagNames = 'html|head|body|title|script|style|link|meta|base|iframe|frame|frameset|object|embed|applet|form|input|button|select|option|textarea|label|table|thead|tbody|tfoot|tr|td|th|caption|col|colgroup|div|span|p|br|hr|img|a|svg|math|video|audio|source|track|canvas|map|area|marquee|b|i|u|s|em|strong|small|big|font|center|sup|sub|pre|code|blockquote|cite|abbr|article|aside|details|summary|dialog|figure|figcaption|footer|header|main|nav|section|template|noscript|plaintext|xmp|layer|basefont|isindex|keygen|menuitem|param';
const HtmlTagReg = new RegExp(`<\\s*\\/?\\s*(?:${HtmlTagNames})\\b[^>]*>`, 'i');
export const PhoneReg = /^1[3-9]\d{9}$/;
export const EmailReg = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/** 是否为非法字符入参（控制字符 / HTML 标签） */
export function isIllegalText(value) {
  if (!value) {
    return false;
  }
  return ControlCharReg.test(value) || HtmlTagReg.test(value);
}
/**
 * 文本类字段的通用校验规则：必填 + 长度上限（兜底）+ 非法字符。
 * 输入框侧的 maxLength 已经先限制住长度，这里的长度校验主要针对
 * Select allowCreate、回填旧数据等 maxLength 管不到的场景。
 *
 * @param t useLocale 返回的文案对象
 * @param options 长度上限等配置
 */
export function getTextRules(t, options = {}) {
  const {
    max = InputLimit.name,
    maxBytes,
    required = false,
    allowHtml = false
  } = options;
  const rules = [];
  if (required) {
    rules.push({
      required: true,
      message: t['public.validate.required']
    });
  }
  rules.push({
    validator: (value, callback) => {
      const text = value === undefined || value === null ? '' : String(value);
      if (!text) {
        callback();
        return;
      }
      if (charLength(text) > max) {
        callback(t['public.validate.max'].replace('{max}', String(max)));
        return;
      }
      if (maxBytes && byteLength(text) > maxBytes) {
        callback(t['public.validate.maxBytes'].replace('{max}', String(maxBytes)).replace('{maxCjk}', String(Math.floor(maxBytes / 3))));
        return;
      }
      if (ControlCharReg.test(text)) {
        callback(t['public.validate.illegal']);
        return;
      }
      if (!allowHtml && HtmlTagReg.test(text)) {
        callback(t['public.validate.html']);
        return;
      }
      callback();
    }
  });
  return rules;
}

/** 手机号校验规则 */
export function getPhoneRules(t, required = false) {
  return [...(required ? [{
    required: true,
    message: t['public.validate.required']
  }] : []), {
    validator: (value, callback) => {
      const text = value === undefined || value === null ? '' : String(value);
      if (!text) {
        callback();
        return;
      }
      if (!PhoneReg.test(text)) {
        callback(t['public.validate.phone']);
        return;
      }
      callback();
    }
  }];
}

/** 邮箱校验规则（允许为空，长度上限默认与 contact_email 列宽一致） */
export function getEmailRules(t, required = false, max = InputLimit.title) {
  return [...(required ? [{
    required: true,
    message: t['public.validate.required']
  }] : []), {
    validator: (value, callback) => {
      const text = value === undefined || value === null ? '' : String(value);
      if (!text) {
        callback();
        return;
      }
      if (charLength(text) > max) {
        callback(t['public.validate.max'].replace('{max}', String(max)));
        return;
      }
      if (!EmailReg.test(text)) {
        callback(t['public.validate.email']);
        return;
      }
      callback();
    }
  }];
}